import {
  buildPath,
  buildTemplatePath,
  Card,
  CloseButton,
  deepCopy,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  StaffBulkCreateServices,
  StaffService,
} from "generated/openApi";
import produce from "immer";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { SelectedStaffs } from "./SelectedStaffs";
import { ServiceInfo } from "./ServiceInfo";

const routeName = "staffbulkCreateServices";

export const staffBulkCreateServicesPageRoute: PageRoute = {
  handle: "New Staff Services",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StaffBulkCreateServicesPageState = {
  selectedIds: number[];
};

export const StaffBulkCreateServicesPage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StaffBulkCreateServicesPageState>(state);
  const [bulkCreateServices, setBulkCreateServices] = useState<StaffBulkCreateServices>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkCreateServices] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkCreateServices(stateParam.selectedIds);
  }, []);

  const getBulkCreateServices = (staffIds: number[]) => {
    ApiService.staff.getBulkCreateServices(staffIds).then((response) => {
      setBulkCreateServices(response);
      resetUnsavedChanges([response]);
    });
  };

  const save = (bulkCreateServices: StaffBulkCreateServices) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.staff
      .saveBulkCreateServices(bulkCreateServices)
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkCreateServices]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{staffBulkCreateServicesPageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton onClick={() => save(bulkCreateServices)} disabled={!unsavedChanges} />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="lg">
        <Card header="Services">
          <Stack direction="column" spacing="md">
            <ServiceInfo
              service={bulkCreateServices.service || ({} as StaffService)}
              setService={(value) =>
                setBulkCreateServices(
                  produce(bulkCreateServices, (draft) => {
                    draft.service = value;
                  })
                )
              }
              validationErrors={validationErrors[pn<StaffBulkCreateServices>("service")] || {}}
            />
          </Stack>
        </Card>

        <SelectedStaffs
          staffs={new Map(bulkCreateServices.staffs?.map((x) => [x.id, x]))}
          setStaffs={(value) => setBulkCreateServices({ ...bulkCreateServices, staffs: Array.from(value.values()) })}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
